import Icon, { ICONS } from "@/components/Core/Icons";
import { Skeleton } from "@/components/ui/skeleton";
import { useSaveStoryboard } from "@/hooks/storyboards/useSaveStoryboard";
import { cn } from "@/lib/utils";
import { CursorType } from "@/store/globalSettingsSlice";
import {
  clearInternalErrorForSb,
  selectMultiSelectedTempoElements,
  selectSelectedTempoElement,
  sendMessageToIFrame,
  setHoveredElementKey,
  setMultiSelectWithElementKey,
  setRequestedContextPosition,
  setSelectedElementKey,
  setShowContextMenu,
} from "@/store/iframeSlice";
import { useAiActionStore } from "@/store/zustand/aiActionStore";
import { useChangeLedgerStore } from "@/store/zustand/changeLedger";
import {
  useCanvasStore,
  useCanvasZoomRealtime,
} from "@/store/zustand/canvasStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import useLayoutStore from "@/store/zustand/layoutStore";
import { ACTION_TYPES } from "@/utils/ai/utils";
import {
  EMPTY_TREE_CODEBASE_ID,
  STORYBOARD_HYDRATION_STATUS,
  STORYBOARD_STATUS,
  STORYBOARD_TYPES,
  STORYBOARD_TYPES_WITH_MINIMAL_HEADER,
} from "@/utils/constants";
import { ProjectRunMode } from "@/utils/templateProjects";
import { TempoElement } from "@/utils/tempoElement";
import { Storyboard } from "@/utils/types";
import {
  useActiveCanvas,
  useActiveProject,
  useCanvasId,
  usePrevious,
} from "@/utils/utils";
import {
  ArrowLeft,
  ArrowRight,
  Diamond,
  ExpandIcon,
  ExternalLinkIcon,
  Info,
  Loader2,
  RefreshCcw,
} from "lucide-react";
import React, {
  MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "react-tooltip";
import { Button } from "../ui/button";
import { useFullscreenToSize } from "./hooks/useFullscreenToSize";
import useUpdateStoryboardName from "./hooks/useUpdateStoryboardName";
import IframeComponent from "./StaticIframeComponent";
import { StoryboardPlaceholderControls } from "./StoryboardPlaceholderControls";
import StoryboardResizeMenu from "./StoryboardResizeMenu";
import { CANVAS_HEIGHT, CANVAS_WIDTH, StoryboardChangeType } from "./utils";
import { track } from "@/utils/events/tracker";
import { useErrorsForStoryboard } from "./hooks/useStoryboardErrors";
import { useSupabase } from "../contexts/DatabaseSyncWrapper";
import { useDevServerStore } from "@/store/zustand/devServerStore";
import { getTempobookUrlFilePath } from "@/utils/sharedUtils";
import { useSession } from "@clerk/clerk-react";

const STORYBOARD_REFRESH_INTERVAL = 3000;
const STORYBOARD_NAV_LOAD_TIMEOUT = 5000;
export interface Props {
  storyboard: Storyboard;

  forceShowOverlay: boolean;
  allowHover: boolean;

  // For moving & resizing the storyboard
  startStoryboardChange: (
    storyboard: any,
    changeType: StoryboardChangeType,
    clientX: number,
    clientY: number
  ) => void;

  // For marking the start of drawing an object
  startDrawingObject: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;

  // Note, there are other changes that could be happening that can are ended by endAllChanges
  endAllChanges: () => void;

  onLoadIFrame: (
    e: any,
    storyboard: any,
    originalStoryboardUrl: string
  ) => void;
  iframeRef: (storyboard: any, ref: any) => void;

  // TODO: SHOULD REMOVE, TEMPORARY UNTIL CODE IS BETTER
  dragToSelect: any;
  changeStoryboardId: string;
  changeType?: StoryboardChangeType;
  droppingStoryboard: boolean;

  canvasContainerRef: MutableRefObject<any>;

  // Index in canvas
  index: number;

  hidden?: boolean;
}

export const StoryboardView = React.memo(
  ({
    storyboard,
    forceShowOverlay,
    allowHover,
    startStoryboardChange,
    startDrawingObject,
    endAllChanges,
    onLoadIFrame,
    iframeRef,
    dragToSelect,
    changeStoryboardId,
    changeType,
    droppingStoryboard,
    canvasContainerRef,
    index,
    hidden,
  }: Props) => {
    const dispatch = useDispatch<any>();
    const canvasId = useCanvasId();

    const selectedElement = useSelector(selectSelectedTempoElement);
    const multiselectedElements = useSelector(selectMultiSelectedTempoElements);

    const storyboardElementKeyRef = useRef(
      TempoElement.forStoryboard(storyboard.id).getKey()
    );

    // Make sure we're only reacting to if THIS storyboard switched its hover state,
    // not any other storyboard, as hover states change during panning/zooming
    // which needs to maintain a smooth FPS
    const storyboardHovered = useSelector(
      (state: any) =>
        state.iframe.hoveredElementKey === storyboardElementKeyRef.current
    );

    const navTreeForThisStoryboard = useCanvasStore(
      (store) => store.storyboardToNavTree[storyboard.id]
    );
    const isEmptyNavTree =
      navTreeForThisStoryboard &&
      navTreeForThisStoryboard?.tempoElement.codebaseId ===
        EMPTY_TREE_CODEBASE_ID;

    const { supabaseClient } = useSupabase();

    const activeCanvas = useActiveCanvas();

    const storyboardStatuses = useSelector(
      (state: any) => state.codebase.storyboardStatuses
    );
    const storyboardHref = useSelector(
      (state: any) => state.iframe.storyboardIdToHref[storyboard.id]
    );
    const storyboardIdToInternalError = useSelector(
      (state: any) => state.iframe.storyboardIdToInternalError[storyboard.id]
    );

    const { anyErrorForThisStoryboard, internalErrorForThisStoryboard } =
      useErrorsForStoryboard(storyboard.id);

    const internalErrorRef = useRef<any>(internalErrorForThisStoryboard);
    useEffect(() => {
      internalErrorRef.current = internalErrorForThisStoryboard;
    }, [internalErrorForThisStoryboard]);

    const { session } = useSession();

    const prevInternalErrorForThisStoryboard: any = usePrevious(
      internalErrorForThisStoryboard
    );

    const cursorType = useSelector(
      (state: any) => state.globalSettings.cursorType
    );

    const zoomPerc = useCanvasZoomRealtime();

    const project = useActiveProject();

    const aiActionType = useAiActionStore((state) => state.actionType);

    const setStoryboardHtml = useCanvasStore(
      (store) => store.storyboardIdToSetHTML[storyboard.id]
    );
    const isMetaPressed = useCanvasStore((state) => state.isMetaPressed);
    const isCtrlPressed = useCanvasStore((state) => state.isCtrlPressed);

    const setTriggerFlush = useChangeLedgerStore(
      (state) => state.setTriggerFlush
    );

    const fullscreenToSize = useFullscreenToSize();
    const { saveStoryboard, renderConfirmSaveAsModal } = useSaveStoryboard();

    const setNewLayout = useLayoutStore((state) => state.setNewLayout);

    const projectLaunchStatus = useCodebaseStore(
      (state) => state.projectLaunchStatus
    );

    const setMenu = useCanvasStore((state) => state.setMenu);

    const [showStoryboardControls, setShowStoryboardControls] = useState(false);

    const canvasIdToDevServerUrl = useSelector(
      (state: any) => state.iframe.canvasIdToDevServerUrl
    );
    const devServerUrl = canvasIdToDevServerUrl[canvasId];

    const [sentEventForSbNotLoaded, setSentEventForSbNotLoaded] =
      useState(false);

    // To override the dev server url
    const [overrideUrl, setOverrideUrl] = useState(null);

    // For locally triggereing refresh on certain storyboards
    const [refreshKey, setRefreshKey] = useState(0);

    // Globally trigger a refresh
    const forceReloadKey = useSelector(
      (state: any) => state.iframe.forceReloadKey
    );

    // Notification that the iframe likely changed (e.g. after an API request)
    const iframeChangedKey = useSelector(
      (state: any) => state.iframe.iframeChangedKey
    );

    const clearNavTreeForStoryboard = useCanvasStore(
      (store) => store.clearNavTreeForStoryboard
    );

    const frozenStatusForThisStoryboard = useCanvasStore(
      (store) => store.storyboardIdToStatus[storyboard.id]
    );

    const setStoryboardIdToStatus = useCanvasStore(
      (store) => store.setStoryboardIdToStatus
    );
    const setFullHtmlForStoryboard = useCanvasStore(
      (store) => store.setFullHtmlForStoryboard
    );
    const setStoryboardIdToError = useDevServerStore(
      (store) => store.setStoryboardIdToError
    );

    const draggingComponent = useSelector(
      (state: any) => state.iframe.draggingComponent
    );

    const minZIndex = useMemo(() => {
      return activeCanvas?.storyboards
        ?.map((storyboard: any) => storyboard.zIndex)
        ?.reduce((a: any, b: any) => Math.min(a, b), Infinity);
    }, [activeCanvas]);

    const newlyDrawingStoryboard = useMemo(() => {
      return (
        !storyboard.created && storyboard.type !== STORYBOARD_TYPES.APPLICATION
      );
    }, [storyboard]);

    const storyboardSelected = useMemo(() => {
      return (
        selectedElement.isStoryboard(storyboard.id) ||
        multiselectedElements?.find((tempoElement) =>
          tempoElement.isStoryboard(storyboard.id)
        )
      );
    }, [selectedElement, storyboard, multiselectedElements]);

    const storyboardLoaded = useMemo(() => {
      if (storyboard.designSuggestionId && !storyboard.created) {
        return true;
      }

      if (storyboard.isPlaceholder || storyboard.aiGenerating) {
        return true;
      }

      return (
        (navTreeForThisStoryboard &&
          !newlyDrawingStoryboard &&
          (storyboard.created ||
            storyboard.type === STORYBOARD_TYPES.APPLICATION)) ||
        setStoryboardHtml
      );
    }, [
      navTreeForThisStoryboard,
      newlyDrawingStoryboard,
      storyboard,
      setStoryboardHtml,
    ]);

    const storyboardLoadedContent = useMemo(() => {
      return (
        navTreeForThisStoryboard &&
        (storyboard.created || storyboard.type === STORYBOARD_TYPES.APPLICATION)
      );
    }, [
      navTreeForThisStoryboard,
      newlyDrawingStoryboard,
      storyboard,
      setStoryboardHtml,
    ]);

    const [storyboardEverLoadedContent, setStoryboardEverLoadedContent] =
      useState(false);
    useEffect(() => {
      if (storyboardLoadedContent) {
        setStoryboardEverLoadedContent(true);
      }
    }, [storyboardLoadedContent]);

    const turnStoryboardLive = (load?: boolean) => {
      setStoryboardIdToStatus(storyboard.id, {
        status: load ? "loading" : "live",
        last_updated: new Date(),
      });
    };

    const prevForceReloadKey = usePrevious(forceReloadKey);
    useEffect(() => {
      // If force reloading, set storyboard to offline
      if (forceReloadKey && prevForceReloadKey !== forceReloadKey) {
        setStoryboardIdToStatus(storyboard.id, {
          status: "offline",
          last_updated: new Date(),
        });
      }
    }, [forceReloadKey]);

    // Storyboard online/offline status
    const htmlForThisStoryboard = useCanvasStore(
      (store) => store.storyboardIdToFullHTML[storyboard.id]
    );

    /**
     * When contents load, turn the storyboard live
     */
    useEffect(() => {
      if (
        storyboardLoadedContent &&
        !isEmptyNavTree &&
        htmlForThisStoryboard &&
        (!frozenStatusForThisStoryboard ||
          frozenStatusForThisStoryboard?.status === "offline" ||
          frozenStatusForThisStoryboard?.status === "loading")
      ) {
        turnStoryboardLive();
      }
    }, [htmlForThisStoryboard]);

    /**
     * When the user selects an element inside the storyboard or the storyboard itself, turn it live
     */
    useEffect(() => {
      if (
        selectedElement?.storyboardId === storyboard.id &&
        frozenStatusForThisStoryboard?.status === "frozen"
      ) {
        turnStoryboardLive(true);
      }
    }, [selectedElement]);

    useEffect(() => {
      if (
        storyboard?.aiGenerating &&
        frozenStatusForThisStoryboard?.status === "frozen"
      ) {
        turnStoryboardLive(true);
      }
    }, [storyboard?.aiGenerating]);

    /**
     * If trying to drop or draw in this storyboard, load it
     */
    useEffect(() => {
      if (
        (droppingStoryboard ||
          draggingComponent ||
          cursorType === CursorType.Draw ||
          cursorType === CursorType.Text) &&
        storyboardHovered &&
        frozenStatusForThisStoryboard?.status === "frozen"
      ) {
        turnStoryboardLive(true);
      }
    }, [cursorType, droppingStoryboard, draggingComponent, storyboardHovered]);

    const thisStoryboardStatus = frozenStatusForThisStoryboard?.status;

    useEffect(() => {
      if (!storyboardLoaded && thisStoryboardStatus !== "frozen") {
        // @ts-expect-error TS2345
        setFullHtmlForStoryboard(storyboard.id, null);
        setStoryboardIdToStatus(storyboard.id, {
          status: "offline",
          last_updated: new Date(),
        });
      } else {
        // @ts-expect-error TS2345
        setStoryboardIdToError(storyboard.id, null);
        dispatch(clearInternalErrorForSb({ storyboardId: storyboard.id }));
      }
    }, [storyboardLoaded]);

    useEffect(() => {
      if (anyErrorForThisStoryboard && supabaseClient) {
        const clearHtmlForSbInSupabase = async () => {
          await supabaseClient
            .from("saved_storyboard_html")
            .upsert(
              {
                storyboard_id: storyboard.id,
                html: "",
                canvas_id: canvasId,
                last_frozen_at: null,
              },
              { onConflict: "storyboard_id" }
            )
            .eq("storyboard_id", storyboard.id);
        };

        clearHtmlForSbInSupabase();
        // @ts-expect-error TS2345
        setFullHtmlForStoryboard(storyboard.id, null);
      }
    }, [anyErrorForThisStoryboard]);

    useEffect(() => {
      if (!isEmptyNavTree) {
        // @ts-expect-error TS2345
        setStoryboardIdToError(storyboard.id, null);
        dispatch(clearInternalErrorForSb({ storyboardId: storyboard.id }));
      }
    }, [navTreeForThisStoryboard]);

    useEffect(() => {
      if (!storyboardLoaded && thisStoryboardStatus !== "frozen") {
        // @ts-expect-error TS2345
        setFullHtmlForStoryboard(storyboard.id, null);
        setStoryboardIdToStatus(storyboard.id, {
          status: "offline",
          last_updated: new Date(),
        });
      }
    }, [thisStoryboardStatus]);

    const storyboardLoadedRef = useRef(storyboardLoaded);
    useEffect(() => {
      storyboardLoadedRef.current = storyboardLoaded;
    }, [storyboardLoaded]);

    const hiddenRef = useRef(hidden);
    useEffect(() => {
      hiddenRef.current = hidden;
    }, [hidden]);

    // When set to true, need to check if the storyboard has loaded as time has run out
    const [refreshStoryboardNotLoaded, setRefreshStoryboardNotLoaded] =
      useState(false);

    const refreshingStoryboardRef = useRef(false);

    /**
     * On first load clear the nav tree for this storyboard
     */
    useEffect(() => {
      if (storyboard.id) {
        clearNavTreeForStoryboard(storyboard.id);
      }
    }, []);

    /**
     * If the storyboard isn't loaded - see if it reloads after a certain
     * period of time and if not trigger a refresh try
     */
    useEffect(() => {
      if (refreshingStoryboardRef.current || !storyboardEverLoadedContent) {
        return;
      }

      if (!storyboardLoaded && !hidden) {
        const timeoutId = setTimeout(() => {
          if (!storyboardLoadedRef.current && !hiddenRef.current) {
            setRefreshStoryboardNotLoaded(true);
          }
        }, STORYBOARD_NAV_LOAD_TIMEOUT);

        return () => {
          clearTimeout(timeoutId);
        };
      }
    }, [storyboardLoaded, hidden]);

    /**
     * If there is a hydration error, or the nav tree isn't loading,
     * try to refresh the storyboard 5 times every 3 seconds to
     * see if it loads (can happen if it's periodic)
     */
    useEffect(() => {
      if (refreshingStoryboardRef.current) {
        return;
      }

      const currentStatus = internalErrorForThisStoryboard?.status;
      const prevStatus = prevInternalErrorForThisStoryboard?.status;

      const triggerRefresh =
        currentStatus &&
        prevStatus &&
        prevStatus === STORYBOARD_HYDRATION_STATUS.NO_ERROR &&
        currentStatus === STORYBOARD_HYDRATION_STATUS.ERROR;

      if (!triggerRefresh && !refreshStoryboardNotLoaded) {
        return;
      }

      setRefreshStoryboardNotLoaded(false);
      refreshingStoryboardRef.current = true;

      const refresh = () => {
        setRefreshKey((prev) => prev + 1);
      };

      refresh();

      let numTimesRefreshed = 1;
      const interval = setInterval(() => {
        // No more errors, no need to refresh
        const newStatus = internalErrorRef?.current?.status;
        if (
          storyboardLoadedRef.current &&
          newStatus !== STORYBOARD_HYDRATION_STATUS.ERROR
        ) {
          clearInterval(interval);
          refreshingStoryboardRef.current = false;
          return;
        }

        refresh();
        numTimesRefreshed += 1;

        if (numTimesRefreshed === 5) {
          clearInterval(interval);
          refreshingStoryboardRef.current = false;
          return;
        }
      }, STORYBOARD_REFRESH_INTERVAL);
    }, [internalErrorForThisStoryboard, refreshStoryboardNotLoaded]);

    const responsiveCheckIntervalRef = useRef(null);

    /**
     * An responsive check along with an exponentially less frequent interval up to 32 seconds.
     * Returns a function to clear the interval for useEffects
     */
    const responsiveCheckInterval = () => {
      // Only do this check once per canvas, so only for the first storyboard in the canvas
      if (index !== 0) {
        return;
      }

      if (responsiveCheckIntervalRef.current) {
        clearInterval(responsiveCheckIntervalRef.current);
        responsiveCheckIntervalRef.current = null;
      }

      const responsiveCheck = () => {
        dispatch(
          sendMessageToIFrame({
            type: "executeFunction",
            payload: {
              functionName: "checkIfHydrationError",
              args: [],
            },
          })
        );
      };

      responsiveCheck();

      // Check every power of 2 second until 32 seconds
      let intervalNum = 1;
      let nextIntervalToCheckAt = 2;

      // @ts-expect-error TS2322
      responsiveCheckIntervalRef.current = setInterval(() => {
        if (intervalNum === nextIntervalToCheckAt) {
          nextIntervalToCheckAt *= 2;
          responsiveCheck();
        }

        if (intervalNum >= 32) {
          // @ts-expect-error TS2769
          clearInterval(responsiveCheckIntervalRef.current);
          return;
        }

        intervalNum += 1;
      }, 1000);

      return () =>
        responsiveCheckIntervalRef.current !== null &&
        clearInterval(responsiveCheckIntervalRef.current);
    };

    /**
     * Periodically, and when the iframe changes, check if every storyboard is accessible by API
     */
    // @ts-expect-error TS2345
    useEffect(() => {
      if (
        !devServerUrl ||
        projectLaunchStatus !== "succeeded" ||
        !session ||
        !activeCanvas
      ) {
        return;
      }

      return responsiveCheckInterval();
    }, [
      forceReloadKey,
      iframeChangedKey,
      devServerUrl,
      projectLaunchStatus,
      Boolean(session),
      activeCanvas?.id,
    ]);

    const [previouslyTriggeredStorybordId, setPreviouslyTriggeredStorybordId] =
      useState(new Set());

    /**
     * Also do the check when the canvas changes, only if there are new storyboards
     * or deleted storyboards
     */
    // @ts-expect-error TS2345
    useEffect(() => {
      if (!devServerUrl || projectLaunchStatus !== "succeeded") {
        return;
      }

      const currentCheckedStoryboards = new Set(
        Object.keys(storyboardIdToInternalError || {})
      );
      previouslyTriggeredStorybordId.forEach((sbId: string) => {
        currentCheckedStoryboards.delete(sbId);
      });

      const currentStoryboards = new Set<string>(
        activeCanvas?.storyboards
          ?.filter(
            (storyboard) =>
              !storyboard.deleted &&
              !storyboard.preCreated &&
              !storyboard.isPlaceholder
          )
          ?.map((storyboard) => storyboard.id)
      );

      const storyboardsNotInChecked = [...currentStoryboards].filter(
        (storyboardId) => !currentCheckedStoryboards.has(storyboardId)
      );

      if (storyboardsNotInChecked?.length) {
        setPreviouslyTriggeredStorybordId(new Set(storyboardsNotInChecked));
        return responsiveCheckInterval();
      } else {
        setPreviouslyTriggeredStorybordId(new Set());
      }
    }, [activeCanvas?.storyboards]);

    const baseStoryboardUrl = useMemo(() => {
      if (!devServerUrl) {
        return "";
      }

      const calculatedTempobookUrlPath = getTempobookUrlFilePath(
        canvasId,
        project,
        storyboard.fullFilePath || "",
        storyboard.type === STORYBOARD_TYPES.STORY
          ? storyboard.storyComponentName || storyboard.name
          : storyboard.storyComponentName
      );

      let url;
      if (storyboard.type === STORYBOARD_TYPES.APPLICATION) {
        url = devServerUrl;
      } else if (!storyboard.created) {
        return "";
      } else {
        url = `${devServerUrl}${calculatedTempobookUrlPath}?storyboard=true&type=${storyboard.type}`;
      }

      if (url && import.meta.env.VITE_DEBUG_LOG_DEVTOOLS) {
        if (url.includes("?")) {
          url = `${url}&debugLog=true`;
        } else {
          url = `${url}?debugLog=true`;
        }
      }

      if (url.includes("?")) {
        url = `${url}&framework=${project?.framework}`;
      } else {
        url = `${url}?framework=${project?.framework}`;
      }

      return url;
    }, [
      devServerUrl,
      project,
      storyboard.type,
      storyboard.created,
      storyboard.fullFilePath,
      storyboard.storyComponentName,
    ]);

    const movingThisStoryboard =
      changeStoryboardId === storyboard.id &&
      changeType === StoryboardChangeType.MOVE;
    const droppingThisStoryboard =
      droppingStoryboard && changeStoryboardId === storyboard.id;

    const prevBaseStoryboardUrl = usePrevious(baseStoryboardUrl);
    useEffect(() => {
      // If the URL of the storyboard has changed under the hood, clear the override url
      if (baseStoryboardUrl !== prevBaseStoryboardUrl) {
        setOverrideUrl(null);
      }
    }, [baseStoryboardUrl]);

    const storyboardUrl = overrideUrl || baseStoryboardUrl;

    // Storyboard status includes compilation errors, server being down
    // hydration errors are client-side errors

    let creationTime = null;

    if (storyboard.created_at) {
      const t = storyboard.created_at;

      // created_at is either
      // a) a Firebase Timestamp (will have _seconds and _nanoseconds properties)
      // b) an ISO string

      if (t._seconds) {
        // @ts-expect-error TS2322
        creationTime = new Date(t._seconds * 1000 + t._nanoseconds / 1000000);
      } else {
        // @ts-expect-error TS2322
        creationTime = new Date(t);
      }
    }

    const storyboardHasError =
      // Has an error
      (storyboardStatuses?.[storyboard.id] === STORYBOARD_STATUS.ERROR ||
        internalErrorForThisStoryboard?.status ===
          STORYBOARD_HYDRATION_STATUS.ERROR ||
        internalErrorForThisStoryboard?.status ===
          STORYBOARD_HYDRATION_STATUS.OTHER_ERROR) &&
      // Newly created storyboards shouldn't show an error for the first 3 seconds
      (!creationTime || new Date().getTime() - creationTime > 3000);

    const showControls =
      (showStoryboardControls ||
        storyboardSelected ||
        storyboardHovered ||
        selectedElement.storyboardId === storyboard.id ||
        storyboardHasError) &&
      !newlyDrawingStoryboard;

    // These are the base conditions in which we want to see events inside the iframe
    // Note that there are other cases (empty nav tree, drawing storyboard, drag to select, etc)
    // when we still don't want to register events inside the iframe
    const needEventsInsideStoryboard =
      selectedElement.storyboardId === storyboard.id ||
      isMetaPressed ||
      isCtrlPressed ||
      cursorType === CursorType.Drive;

    // The overlay prevents any events from being triggered inside the storyboard, and allows users to
    // select the storyboard itself by clicking on it.
    // This should be done if there is nothing inside the storyboard, or if nothing has been selected
    // inside yet, and the user hasn't tried to drill down into the storyboard
    let showStoryboardOverlay =
      isEmptyNavTree ||
      !needEventsInsideStoryboard ||
      newlyDrawingStoryboard ||
      forceShowOverlay ||
      cursorType === CursorType.Draw ||
      cursorType === CursorType.Text ||
      !storyboardLoaded ||
      frozenStatusForThisStoryboard?.status === "frozen";

    // Never show the overlay if in drive mode and it is not frozen
    showStoryboardOverlay =
      showStoryboardOverlay &&
      (cursorType !== CursorType.Drive ||
        frozenStatusForThisStoryboard?.status === "frozen");

    const triggerFullscreen = () => {
      fullscreenToSize(
        canvasContainerRef,
        storyboard.width,
        storyboard.height,
        storyboard.x,
        storyboard.y
      );
    };

    // Set on the storyboard name/header & on the iframe itself if there is no nav tree
    const storyboardControls = {
      onMouseOver: (e) => {
        if (dragToSelect) {
          return;
        }

        // This storyboard shouldn't be handling events other than to end changes
        // if it is being changed
        if (changeStoryboardId === storyboard.id) {
          return;
        }

        const elementForStoryboard = TempoElement.forStoryboard(storyboard.id);

        // In multiselect mode -> don't hover if can't select
        if (
          e.shiftKey &&
          !selectedElement?.isEmpty() &&
          !selectedElement?.isSiblingOf(elementForStoryboard)
        ) {
          return;
        }

        if (allowHover) {
          const newHoveredElementKey = elementForStoryboard.getKey();
          dispatch(setHoveredElementKey(newHoveredElementKey));
        }
      },
      onMouseOut: () => {
        dispatch(setHoveredElementKey(null));
      },
      onMouseDown: async (e) => {
        if (e.button !== 0) {
          return;
        }

        e.stopPropagation();
        e.preventDefault();

        if (e.shiftKey) {
          dispatch(
            setMultiSelectWithElementKey(
              TempoElement.forStoryboard(storyboard.id).getKey()
            )
          );
        } else {
          dispatch(
            setSelectedElementKey(
              TempoElement.forStoryboard(storyboard.id).getKey()
            )
          );
        }

        if (cursorType !== "draw") {
          startStoryboardChange(
            storyboard,
            StoryboardChangeType.MOVE,
            e.clientX,
            e.clientY
          );
        }

        if (cursorType === CursorType.Draw || cursorType === CursorType.Text) {
          await startDrawingObject(e);
        }
      },
      onMouseUp: (e) => {
        e.stopPropagation();
        e.preventDefault();

        endAllChanges();
      },
      onContextMenu: (e) => {
        e.preventDefault();
        e.stopPropagation();

        const left = e.clientX;
        const top = e.clientY;

        setMenu(left, top);
        dispatch(setShowContextMenu(true));

        if (selectedElement.isEmpty()) {
          dispatch(
            setSelectedElementKey(
              TempoElement.forStoryboard(storyboard.id).getKey()
            )
          );
        }
      },
      onClick: () => {
        dispatch(setShowContextMenu(false));
        dispatch(setRequestedContextPosition(null));
      },
    };

    const zoomMultiplier = Math.min(2, 1 / zoomPerc);

    const minimalHeader = useMemo(() => {
      return STORYBOARD_TYPES_WITH_MINIMAL_HEADER.has(storyboard.type);
    }, [storyboard]);

    // If the header is too small only show the play/pencil icon & the name
    const hideExtraControls =
      storyboard.width / zoomMultiplier < 425 ||
      storyboard.isPlaceholder ||
      (minimalHeader && !storyboardSelected);

    const [isEditingName, setIsEditingName] = useState(false);
    const [editedName, setEditedName] = useState(
      storyboard.type === STORYBOARD_TYPES.APPLICATION
        ? storyboardHref || devServerUrl
        : storyboard.name
    );

    const handleNameClick = (e) => {
      if (storyboard.type === STORYBOARD_TYPES.APPLICATION) {
        return;
      }

      setIsEditingName(true);
    };

    const { setName } = useUpdateStoryboardName(activeCanvas);

    // If the size of one icon is greater than half of the storyboard, only show text
    const forceShowOnlyText = 24 * zoomMultiplier > storyboard.width / 2;

    const showSpecialNameStyle = useMemo(() => {
      return (
        ((storyboard.saved && storyboard.savedCodebaseId) ||
          storyboard.isPlaceholder) &&
        minimalHeader
      );
    }, [storyboard, minimalHeader]);

    const refreshLeftRightComponent = (
      <>
        <ArrowLeft
          data-tooltip-id="tooltip"
          data-tooltip-place="top"
          data-tooltip-content="Back"
          className={cn(
            "w-10 h-10 cursor-pointer p-1 select-none focus:outline-none",
            storyboardSelected || storyboardHovered
              ? "text-primary-0 hover:text-primary-100"
              : ""
          )}
          onClick={() => {
            dispatch(
              sendMessageToIFrame({
                type: "executeFunction",
                storyboardId: storyboard.id,
                payload: {
                  functionName: "goBack",
                  args: [],
                },
              })
            );
          }}
        />
        <ArrowRight
          data-tooltip-id="tooltip"
          data-tooltip-place="top"
          data-tooltip-content="Forward"
          className={cn(
            "w-10 h-10 cursor-pointer p-1 select-none focus:outline-none",
            storyboardSelected || storyboardHovered
              ? "text-primary-0 hover:text-primary-100"
              : ""
          )}
          onClick={() => {
            dispatch(
              sendMessageToIFrame({
                type: "executeFunction",
                storyboardId: storyboard.id,
                payload: {
                  functionName: "goForward",
                  args: [],
                },
              })
            );
          }}
        />
        <RefreshCcw
          data-tooltip-id="tooltip"
          data-tooltip-place="top"
          data-tooltip-content="Refresh"
          className={cn(
            "w-10 h-10 cursor-pointer p-1 select-none focus:outline-none",
            storyboardSelected || storyboardHovered
              ? "text-primary-n-600 hover:text-primary-100"
              : ""
          )}
          onClick={() => {
            setTriggerFlush(true);
            setRefreshKey((prev) => prev + 1);
            setOverrideUrl(storyboardHref || storyboardUrl);
          }}
        />
      </>
    );

    const renderMainFrame = () => {
      let frozenComponent = (
        <IframeComponent
          // Reload the iframe when the storyboard is saved as the components have changed
          key={`${storyboard.id}-frozen-state`}
          className={cn(
            "iframe1",
            frozenStatusForThisStoryboard?.status === "loading" && "absolute"
          )}
          style={{
            width: storyboard.width || 0,
            height: storyboard.height || 0,
            overflow: "hidden",
            zIndex: storyboard.zIndex - minZIndex + 1,
          }}
          htmlContent={htmlForThisStoryboard || ""}
        />
      );

      if (frozenStatusForThisStoryboard?.status === "frozen") {
        return frozenComponent;
      }

      return (
        <>
          <iframe
            src={storyboardUrl}
            // Reload the iframe when the storyboard is saved as the components have changed
            key={`${forceReloadKey}-${storyboard.id}-${
              storyboard.type !== STORYBOARD_TYPES.APPLICATION &&
              storyboard.saved
                ? "saved"
                : ""
            }-${refreshKey}]`}
            id={`main-iframe-${storyboard.id}`}
            style={{
              borderRadius: "6px",
              width: storyboard.width || 0,
              height: storyboard.height || 0,
              overflow: "hidden",
              opacity: droppingThisStoryboard ? 0.4 : 1,
              pointerEvents: showStoryboardOverlay ? "none" : undefined,
              background:
                storyboardSelected || !storyboard.isPlaceholder
                  ? !minimalHeader
                    ? "white"
                    : "none"
                  : `
                  repeating-linear-gradient(to right, #6448ef, #6448ef 10px, transparent 10px, transparent 20px) top/100% 2px no-repeat,
                  repeating-linear-gradient(to right, #6448ef, #6448ef 10px, transparent 10px, transparent 20px) bottom/100% 2px no-repeat,
                  repeating-linear-gradient(to bottom, #6448ef, #6448ef 10px, transparent 10px, transparent 20px) left/2px 100% no-repeat,
                  repeating-linear-gradient(to bottom, #6448ef, #6448ef 10px, transparent 10px, transparent 20px) right/2px 100% no-repeat
                  `,
            }}
            className={cn("iframe1", storyboard?.id)}
            onLoad={(e) => {
              onLoadIFrame(e, storyboard, baseStoryboardUrl);
            }}
            ref={(ref) => iframeRef(storyboard, ref)}
            onMouseOut={() => {
              dispatch(setHoveredElementKey(null));
            }}
            onMouseUp={(e) => {
              endAllChanges();
              e.preventDefault();
              e.stopPropagation();
            }}
          />
          {frozenStatusForThisStoryboard?.status === "loading" &&
            !anyErrorForThisStoryboard && (
              <>
                {frozenComponent}
                <div
                  className="absolute bg-black opacity-30"
                  style={{
                    width: storyboard.width || 0,
                    height: storyboard.height || 0,
                    overflow: "hidden",
                    zIndex: storyboard.zIndex - minZIndex + 2,
                  }}
                />
              </>
            )}
          {setStoryboardHtml && (
            <IframeComponent
              // Reload the iframe when the storyboard is saved as the components have changed
              key={`${storyboard.id}-instant-update`}
              className="absolute iframe1"
              style={{
                width: storyboard.width || 0,
                height: storyboard.height || 0,
                overflow: "hidden",
                zIndex: storyboard.zIndex - minZIndex + 2,
              }}
              htmlContent={setStoryboardHtml}
            />
          )}
        </>
      );
    };

    // Either frozen or no longer loading (e.g. drawing, placeholder, etc)
    const storyboardStatusOffline =
      thisStoryboardStatus === "offline" || !thisStoryboardStatus;
    const showGrayDot =
      thisStoryboardStatus === "frozen" ||
      (storyboardStatusOffline && storyboardLoaded);
    const showYellowDot = storyboardStatusOffline && !storyboardLoaded;

    // Show loading spinner logic
    const showLoadingSpinner =
      !(
        // Storyboard has loaded
        (storyboardLoaded && thisStoryboardStatus !== "loading")
      ) &&
      // Storyboard has no error and is not frozen
      !anyErrorForThisStoryboard &&
      thisStoryboardStatus !== "frozen";

    /**
     * Track the storyboard not loaded event
     */
    useEffect(() => {
      let timeout: NodeJS.Timeout | null = null;
      if (!showLoadingSpinner) {
        // Clear the event if the loading spinner is hidden
        setSentEventForSbNotLoaded(false);
      } else if (!sentEventForSbNotLoaded) {
        setSentEventForSbNotLoaded(true);

        // Showing the loading spinner, trigger tracking to posthog if not already sent
        // If it doesn't load in 20 seconds
        timeout = setTimeout(() => {
          track("SB_NOT_LOADED", {
            storyboardId: storyboard.id,
          });
        }, 20000);
      }

      return () => {
        if (timeout) {
          clearTimeout(timeout);
        }
      };
    }, [showLoadingSpinner]);

    return (
      <div
        className={cn(
          "absolute flex flex-col select-none",
          (storyboardSelected || storyboardHovered) &&
            cursorType !== CursorType.Drive &&
            `selected ${aiActionType === ACTION_TYPES.EDIT && "ai-outline"}`
        )}
        key={storyboard.id}
        style={{
          // Middle of the canvas is 0, 0
          left: CANVAS_WIDTH / 2 + storyboard.x,
          top: CANVAS_HEIGHT / 2 + storyboard.y,
          transformOrigin: "top left",

          zIndex: storyboard.zIndex - minZIndex,

          opacity: hidden ? 0 : 1,
          pointerEvents: hidden || droppingThisStoryboard ? "none" : "all",
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="bottom-full relative">
          <div
            className={cn(
              "flex absolute flex-row gap-4 select-none cursor-pointer px-4 bottom-4 rounded-2xl h-16",
              "text-secondary-800 items-center justify-between bg-secondary-300",
              aiActionType === ACTION_TYPES.EDIT && "ai-outline",
              storyboardHovered && "outline-primary-0 outline",
              minimalHeader && "bg-transparent px-0 bottom-0",
              showSpecialNameStyle && "h-20",
              minimalHeader &&
                storyboardHovered &&
                "outline-none outline-transparent text-primary-0"
            )}
            style={{
              width: storyboard.width / zoomMultiplier || 0,
              transform: `scale(${zoomMultiplier})`,
              transformOrigin: "bottom left",
            }}
            {...storyboardControls}
            onMouseEnter={() => setShowStoryboardControls(true)}
            onMouseLeave={() => setShowStoryboardControls(false)}
          >
            <div
              className={cn(
                "w-4 h-4 rounded-full shrink-0",
                anyErrorForThisStoryboard && "!bg-red-500",
                thisStoryboardStatus === "loading" && "bg-blue-500",
                thisStoryboardStatus === "live" && "bg-green-500",
                showGrayDot && "bg-gray-500",
                showYellowDot && "bg-yellow-500"
              )}
            />
            <div
              className={cn(
                "flex flex-row justify-center items-center space-x-1 empty:hidden",
                forceShowOnlyText && "hidden"
              )}
            >
              {storyboard.type === STORYBOARD_TYPES.STORY &&
                !storyboard.saved && (
                  <div className="bg-primary-0 hover:bg-primary-100 cursor-pointer flex justify-center items-center font-semibold text-2xl text-white rounded-sm py-1 px-2 space-x-2 mr-2">
                    <div
                      className="flex justify-center items-center flex-nowrap	overflow-hidden truncate"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        saveStoryboard(storyboard, "story");
                      }}
                      data-tooltip-id="tooltip"
                      data-tooltip-place="top"
                      data-tooltip-content="Click to save this draft story as a new story"
                    >
                      Save Story
                    </div>
                  </div>
                )}
              {!hideExtraControls && !showSpecialNameStyle
                ? refreshLeftRightComponent
                : null}
            </div>
            <div
              className={cn(
                "text-2xl text-center shrink flex-auto min-w-0 flex flex-row items-center justify-start gap-3",
                (storyboardHovered || storyboardSelected) &&
                  "text-primary-n-600",
                minimalHeader && "gap-2"
              )}
            >
              {isEditingName ? (
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onBlur={(e) => {
                    setName(storyboard, editedName);
                    setIsEditingName(false);
                  }}
                  autoFocus
                />
              ) : (
                <div
                  className={cn(
                    "text-ellipsis text-white whitespace-nowrap truncate bg-transparent text-secondary-n-500 rounded-lg flex items-center gap-2.5",
                    showSpecialNameStyle &&
                      "bg-secondary-n-1000 p-3 font-medium",
                    storyboardSelected &&
                      showSpecialNameStyle &&
                      "bg-primary-0 text-white",
                    (storyboardHovered || storyboardSelected) &&
                      !showSpecialNameStyle &&
                      "text-primary-n-600"
                  )}
                  style={forceShowOnlyText ? { overflow: "visible" } : {}}
                  onDoubleClick={handleNameClick}
                >
                  <Diamond
                    className={cn(
                      "size-5 min-w-5 text-primary-n-600 transition-colors",
                      storyboardSelected && "text-white",
                      !(
                        ((storyboard.saved && storyboard.savedCodebaseId) ||
                          storyboard.isPlaceholder) &&
                        minimalHeader
                      ) && "hidden"
                    )}
                  />
                  {(() => {
                    let storyboardLabel =
                      storyboard.type === STORYBOARD_TYPES.APPLICATION
                        ? storyboardHref || devServerUrl
                        : storyboard.name;

                    if (forceShowOnlyText) {
                      storyboardLabel =
                        (storyboardLabel?.substring(0, 1) || "") + "..";
                    }

                    return storyboardLabel;
                  })()}
                </div>
              )}
              {/* If this is the minimal header, refresh left/right are shown to the right of the name */}
              {!hideExtraControls && showSpecialNameStyle
                ? refreshLeftRightComponent
                : null}
              {!hideExtraControls && (
                <ExternalLinkIcon
                  data-tooltip-id="tooltip"
                  data-tooltip-place="top"
                  data-tooltip-content="Preview in Tab"
                  className={cn(
                    "cursor-pointer w-7 h-7 focus:outline-none ml-2",
                    storyboardSelected || storyboardHovered
                      ? "text-primary-n-600 hover:text-primary-100"
                      : ""
                  )}
                  onClick={() => {
                    // Open in new tab
                    // @ts-expect-error TS2531
                    window
                      .open(storyboardHref || storyboardUrl, "_blank")
                      .focus();
                  }}
                />
              )}
            </div>
            {showControls && !hideExtraControls && (
              <StoryboardResizeMenu
                storyboard={storyboard}
                canvas={activeCanvas}
                data-tooltip-id="tooltip"
                data-tooltip-place="top"
                data-tooltip-content="Change size"
                className={cn(
                  "w-10 h-10 cursor-pointer p-1 select-none focus:outline-none",
                  (storyboardHovered || storyboardSelected) &&
                    "text-primary-0 hover:text-primary-100"
                )}
              />
            )}
            {showControls && !hideExtraControls && (
              <div className="flex flex-row justify-center items-center">
                <div className="border-gray-500 rounded-sm flex flex-row justify-center">
                  <ExpandIcon
                    data-tooltip-id="tooltip"
                    data-tooltip-place="top"
                    data-tooltip-content="Expand to fit"
                    className={cn(
                      "w-10 h-10 cursor-pointer p-1 select-none focus:outline-none",
                      (storyboardHovered || storyboardSelected) &&
                        "text-primary-0 hover:text-primary-100"
                    )}
                    onClick={() => triggerFullscreen()}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        {renderMainFrame()}
        {showStoryboardOverlay && (
          <div
            {...storyboardControls}
            className="w-full h-full absolute"
            style={{
              zIndex: storyboard.zIndex - minZIndex + 1,
            }}
          />
        )}
        {(() => {
          const expectedWidth = 60 * zoomMultiplier;
          let allowedMultiplier = zoomMultiplier;

          // Width cannot be bigger than storyboard
          if (expectedWidth > storyboard.width * 0.8) {
            allowedMultiplier = (storyboard.width * 0.8) / 60;
          }

          // Height cannot be bigger than storyboard
          const expectedHeight = 60 * allowedMultiplier;
          if (expectedHeight > storyboard.height * 0.8) {
            allowedMultiplier = (storyboard.height * 0.8) / 60;
          }

          return (
            <div
              className={cn("animate-spin", !showLoadingSpinner && "hidden")}
              style={{
                zIndex: storyboard.zIndex - minZIndex + 2,
                pointerEvents: "none",
                position: "absolute",
                top: "calc(50% - 30px)",
                left: "calc(50% - 30px)",
                boxSizing: "initial",
              }}
            >
              <Loader2
                className="max-w-none w-[60px] h-[60px] stroke-[1.5px] stroke-primary-200"
                style={{
                  transform: `scale(${allowedMultiplier})`,
                  transformOrigin: "center",
                }}
              />
            </div>
          );
        })()}
        {storyboard.aiGenerating ? (
          <div
            className="absolute top-0 left-0 w-full h-full flex items-center justify-center text-sm flex-col"
            style={{
              // Accounts for the border on the iframe in terms of size
              width: storyboard.width || 0,
              height: storyboard.height || 0,
              pointerEvents: "none",
            }}
          >
            <Skeleton className="w-full h-full absolute top-0 left-0"></Skeleton>
            <Icon
              icon={ICONS.AI_LOGO}
              width={48}
              height={48}
              className="opacity-10"
            />
            <div>Generating</div>
          </div>
        ) : null}
        {!storyboard.aiGenerating &&
        storyboard.isPlaceholder &&
        storyboard.designSuggestionId ? (
          <div
            className="absolute top-0 left-0 flex flex-row items-center justify-center text-sm gap-2"
            style={{
              // Accounts for the border on the iframe in terms of size
              width: (storyboard.width || 0) + 2,
              height: (storyboard.height || 0) + 2,
              zIndex: storyboard.zIndex - minZIndex + 3,
            }}
          >
            <StoryboardPlaceholderControls
              storyboardId={storyboard.id}
              small={storyboard.width < 280}
            />
          </div>
        ) : null}
        {(storyboardSelected || storyboardHovered) &&
        cursorType !== CursorType.Drive ? (
          <div
            className={cn(
              "absolute border-2 border-solid left-0 top-0 border-primary-200",
              aiActionType === ACTION_TYPES.EDIT &&
                "border-4 border-solid border-primary-0"
            )}
            style={{
              // Accounts for the border on the iframe in terms of size
              width: (storyboard.width || 0) / zoomMultiplier,
              height: (storyboard.height || 0) / zoomMultiplier,
              zIndex: storyboard.zIndex - minZIndex + 2,
              pointerEvents: "none",
              borderStyle: droppingStoryboard ? "dashed" : "solid",
              transform: `scale(${zoomMultiplier})`,
              transformOrigin: "top left",
            }}
          >
            {[
              StoryboardChangeType.RESIZE_TOP_LEFT,
              StoryboardChangeType.RESIZE_TOP_RIGHT,
              StoryboardChangeType.RESIZE_BOTTOM_RIGHT,
              StoryboardChangeType.RESIZE_BOTTOM_LEFT,
            ].map((changeType) => {
              // 12px = w-3
              const expectedResizerWidth = 12 * zoomMultiplier;
              if (
                expectedResizerWidth * 2 > storyboard.width ||
                expectedResizerWidth * 2 > storyboard.height
              ) {
                return null;
              }

              const positionStyles: any = {};
              switch (changeType) {
                case StoryboardChangeType.RESIZE_TOP_LEFT:
                  positionStyles.top = -7;
                  positionStyles.left = -7;
                  positionStyles.cursor = "nwse-resize";
                  break;
                case StoryboardChangeType.RESIZE_TOP_RIGHT:
                  positionStyles.top = -7;
                  positionStyles.right = -7;
                  positionStyles.cursor = "nesw-resize";
                  break;
                case StoryboardChangeType.RESIZE_BOTTOM_RIGHT:
                  positionStyles.bottom = -7;
                  positionStyles.right = -7;
                  positionStyles.cursor = "nwse-resize";
                  break;
                case StoryboardChangeType.RESIZE_BOTTOM_LEFT:
                  positionStyles.bottom = -7;
                  positionStyles.left = -7;
                  positionStyles.cursor = "nesw-resize";
                  break;
              }

              return (
                <div
                  key={"resize-" + changeType}
                  className="absolute w-3 h-3 bg-[white]"
                  style={{
                    position: "absolute",
                    pointerEvents:
                      movingThisStoryboard || hidden ? "none" : "initial",
                    outline: "4px solid var(--primary-200)",
                    ...positionStyles,
                  }}
                  onMouseDown={(e) => {
                    startStoryboardChange(
                      storyboard,
                      changeType,
                      e.clientX,
                      e.clientY
                    );
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onMouseUp={(e) => {
                    endAllChanges();
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                />
              );
            })}
            {[
              StoryboardChangeType.RESIZE_TOP,
              StoryboardChangeType.RESIZE_LEFT,
              StoryboardChangeType.RESIZE_BOTTOM,
              StoryboardChangeType.RESIZE_RIGHT,
            ].map((changeType) => {
              const positionStyles: any = {};
              switch (changeType) {
                case StoryboardChangeType.RESIZE_TOP:
                  positionStyles.top = -6;
                  positionStyles.left = 0;
                  positionStyles.width = "100%";
                  positionStyles.height = "12px";
                  positionStyles.cursor = "ns-resize";
                  break;
                case StoryboardChangeType.RESIZE_LEFT:
                  positionStyles.top = 0;
                  positionStyles.left = -6;
                  positionStyles.height = "100%";
                  positionStyles.width = "12px";
                  positionStyles.cursor = "ew-resize";
                  break;
                case StoryboardChangeType.RESIZE_BOTTOM:
                  positionStyles.bottom = -6;
                  positionStyles.left = 0;
                  positionStyles.width = "100%";
                  positionStyles.height = "12px";
                  positionStyles.cursor = "ns-resize";
                  break;
                case StoryboardChangeType.RESIZE_RIGHT:
                  positionStyles.top = 0;
                  positionStyles.right = -6;
                  positionStyles.height = "100%";
                  positionStyles.width = "12px";
                  positionStyles.cursor = "ew-resize";
                  break;
              }

              return (
                <div
                  key={"resize" + changeType}
                  className="absolute opacity-0"
                  style={{
                    pointerEvents:
                      movingThisStoryboard || hidden ? "none" : "initial",
                    ...positionStyles,
                  }}
                  onMouseDown={(e) => {
                    startStoryboardChange(
                      storyboard,
                      changeType,
                      e.clientX,
                      e.clientY
                    );
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onMouseUp={(e) => {
                    endAllChanges();
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                />
              );
            })}
            <div className="absolute left-[50%] bottom-[-45px]">
              <div
                className={cn(
                  "rounded-lg text-white text-lg px-2 py-0.5 text-nowrap relative left-[-50%]",
                  aiActionType === ACTION_TYPES.GENERATE
                    ? "bg-primary-0"
                    : "bg-primary-200"
                )}
              >
                {Math.round(storyboard.width)} x {Math.round(storyboard.height)}
              </div>
            </div>
          </div>
        ) : null}

        {renderConfirmSaveAsModal()}
      </div>
    );
  }
);
StoryboardView.displayName = "StoryboardView";
